import React, { createContext, useContext, useState } from 'react';
import { toast } from 'sonner';

const CartContext = createContext(null);

export const CartProvider = ({ children }) => {
  const [items, setItems] = useState([]);
  const [isOpen, setIsOpen] = useState(false);

  const addToCart = (product, selectedSize = null) => {
    const size = selectedSize || (product.pricingType === 'sizewise' ? product.sizes?.[0] : null);
    const price = size ? size.price : product.price;
    const key = `${product._id}-${size?.label || 'single'}`;
    const isExisting = items.some(i => i.key === key);

    setItems(prev => {
      const existing = prev.find(i => i.key === key);
      if (existing) {
        return prev.map(i => i.key === key ? { ...i, quantity: i.quantity + 1 } : i);
      }
      return [...prev, {
        key,
        productId: product._id,
        productName: product.name,
        image: product.images?.[0] || '',
        size: size?.label || '',
        price,
        quantity: 1,
      }];
    });

    // Fixed id so a new toast replaces any existing one instead of stacking;
    // duration set to auto-dismiss in ~2.5s. Called outside setItems so
    // React.StrictMode's double-invoked updater never double-fires this.
    toast.success(isExisting ? `${product.name} quantity updated` : `${product.name} added to cart`, {
      id: 'cart-toast',
      duration: 2500,
    });
  };

  const removeFromCart = (key) => setItems(prev => prev.filter(i => i.key !== key));

  const updateQuantity = (key, qty) => {
    if (qty <= 0) return removeFromCart(key);
    setItems(prev => prev.map(i => i.key === key ? { ...i, quantity: qty } : i));
  };

  const clearCart = () => setItems([]);

  const subtotal = items.reduce((sum, i) => sum + i.price * i.quantity, 0);
  const itemCount = items.reduce((sum, i) => sum + i.quantity, 0);

  return (
    <CartContext.Provider value={{
      items, addToCart, removeFromCart, updateQuantity, clearCart,
      subtotal, itemCount, isOpen, setIsOpen,
    }}>
      {children}
    </CartContext.Provider>
  );
};

export const useCart = () => useContext(CartContext);
